/**
 * SMILES Parser Module
 * Phase 200.9: Pure modular system loading
 * 
 * Converts SMILES chemical notation to XYZ format via PubChem API
 * Supports multi-fragment SMILES (e.g., "CCO.COC") for landing page integration
 */

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate SMILES string format
 */
export function isValidSMILES(str: unknown): str is string {
  if (!str || typeof str !== 'string') return false;
  
  const s = str.trim();
  if (!s) return false;
  
  // Whitelist of characters commonly in SMILES notation
  // Includes `.` for multi-fragment SMILES (e.g., "CCO.COC" for comparing molecules)
  // Blocks spaces and special chars that aren't part of SMILES
  return /^[A-Za-z0-9@+\-=#()\[\]\/\\.]+$/.test(s);
}

// ---------------------------------------------------------------------------
// Multi-fragment SMILES Support
// ---------------------------------------------------------------------------

/**
 * Split SMILES by `.` separator, respecting brackets.
 * e.g., "CCO.[Fe].CC" -> ["CCO", "[Fe]", "CC"]
 * but "[Cu.]" stays as one fragment (. is inside brackets)
 */
export function splitSmilesFragments(smiles: string): string[] {
  const fragments: string[] = [];
  let current = '';
  let bracketDepth = 0;

  for (const char of smiles) {
    if (char === '[') {
      bracketDepth++;
      current += char;
    } else if (char === ']') {
      bracketDepth--;
      current += char;
    } else if (char === '.' && bracketDepth === 0) {
      // Fragment separator
      if (current.trim()) {
        fragments.push(current.trim());
      }
      current = '';
    } else {
      current += char;
    }
  }

  // Don't forget the last fragment
  if (current.trim()) {
    fragments.push(current.trim());
  }

  return fragments;
}

/**
 * Check if SMILES is a single atom in brackets, e.g. [Fe], [Cu], [Pt+2], [Fe2+]
 * Returns the element symbol if it's a single atom, null otherwise.
 */
export function extractSingleAtomElement(smiles: string): string | null {
  const trimmed = smiles.trim();
  // Match [Element] with optional charge like [Fe], [Cu+], [Pt+2], [Fe2+], [O-]
  const match = trimmed.match(/^\[([A-Z][a-z]?)(?:[0-9]*[+-]?[0-9]*|[+-][0-9]*)?\]$/);
  return match ? match[1] : null;
}

// ---------------------------------------------------------------------------
// Atom Types
// ---------------------------------------------------------------------------

interface Atom {
  sym: string;
  x: number;
  y: number;
  z: number;
}

// ---------------------------------------------------------------------------
// PubChem API
// ---------------------------------------------------------------------------

/**
 * Build PubChem API URL for SMILES lookup
 */
function buildPubChemURL(smiles: string): string {
  const encoded = encodeURIComponent(smiles.trim());
  // Request 3D SDF format from PubChem PUG REST API
  return `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/smiles/${encoded}/SDF?record_type=3d`;
}

/**
 * Parse SDF (Structure Data File) mol block to atoms array
 */
function parseSdfToAtoms(sdfText: string): Atom[] {
  const lines = sdfText.split(/\r?\n/);
  
  // Find counts line (usually 4th line in V2000 mol block, contains "V2000")
  let countsIdx = -1;
  for (let i = 0; i < Math.min(20, lines.length); i++) {
    if (/V2000/.test(lines[i]) && i >= 3) {
      countsIdx = i;
      break;
    }
  }
  
  if (countsIdx < 0) {
    throw new Error('SDF parse failed: counts line not found');
  }
  
  // Parse atom count from counts line
  const countsLine = lines[countsIdx];
  let natoms = parseInt(countsLine.slice(0, 3).trim(), 10);
  
  // Fallback: try whitespace split if fixed-width parsing failed
  if (!Number.isFinite(natoms)) {
    const parts = countsLine.trim().split(/\s+/);
    natoms = parseInt(parts[0], 10);
  }
  
  if (!Number.isFinite(natoms) || natoms <= 0) {
    throw new Error('SDF parse failed: invalid atom count');
  }
  
  // Parse atom block (starts after counts line)
  const atomStart = countsIdx + 1;
  const atomLines = lines.slice(atomStart, atomStart + natoms);
  
  const atoms: Atom[] = [];
  
  for (const line of atomLines) {
    const parts = line.trim().split(/\s+/);
    
    if (parts.length < 4) {
      throw new Error('SDF parse failed: malformed atom line');
    }
    
    const x = parseFloat(parts[0]);
    const y = parseFloat(parts[1]);
    const z = parseFloat(parts[2]);
    const sym = parts[3];
    
    if (![x, y, z].every(Number.isFinite)) {
      throw new Error('SDF parse failed: invalid coordinates');
    }
    
    atoms.push({ sym, x, y, z });
  }
  
  return atoms;
}

/**
 * Fetch a single SMILES fragment from PubChem and return atoms.
 * For single bracketed atoms (like [Fe]), generate directly without network call.
 */
async function fetchSmilesFragment(smiles: string): Promise<Atom[]> {
  // Optimization: single bracketed atoms don't need PubChem
  const singleElement = extractSingleAtomElement(smiles);
  if (singleElement) {
    return [{ sym: singleElement, x: 0, y: 0, z: 0 }];
  }

  const url = buildPubChemURL(smiles);
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`PubChem API failed for "${smiles}": ${response.status} ${response.statusText}`);
  }
  
  const sdfText = await response.text();
  return parseSdfToAtoms(sdfText);
}

// ---------------------------------------------------------------------------
// Geometry utilities
// ---------------------------------------------------------------------------

function getBoundingBox(atoms: Atom[]): {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
} {
  if (atoms.length === 0) {
    return { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } };
  }
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const a of atoms) {
    if (a.x < minX) minX = a.x;
    if (a.y < minY) minY = a.y;
    if (a.z < minZ) minZ = a.z;
    if (a.x > maxX) maxX = a.x;
    if (a.y > maxY) maxY = a.y;
    if (a.z > maxZ) maxZ = a.z;
  }
  return { min: { x: minX, y: minY, z: minZ }, max: { x: maxX, y: maxY, z: maxZ } };
}

function atomsToXYZ(atoms: Atom[], comment = 'Generated from SMILES via PubChem'): string {
  const lines: string[] = [];
  lines.push(String(atoms.length));
  lines.push(comment);
  for (const atom of atoms) {
    lines.push(`${atom.sym} ${atom.x} ${atom.y} ${atom.z}`);
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Main API
// ---------------------------------------------------------------------------

const MOLECULE_SPACING = 3.0; // Angstroms between molecules

/**
 * Convert SMILES string to XYZ format via PubChem API.
 * Handles multi-fragment SMILES (with . separator) by spacing them apart.
 */
export async function smilesToXYZ(smiles: string): Promise<string> {
  if (!isValidSMILES(smiles)) {
    throw new Error(`Invalid SMILES format: ${smiles}`);
  }
  
  const trimmed = smiles.trim();
  const fragments = splitSmilesFragments(trimmed);

  if (fragments.length === 1) {
    // Single molecule - direct fetch
    const atoms = await fetchSmilesFragment(fragments[0]);
    return atomsToXYZ(atoms);
  }

  // Multi-molecule: fetch each fragment and combine with spacing
  const allAtoms: Atom[] = [];
  let xOffset = 0;

  for (const frag of fragments) {
    const atoms = await fetchSmilesFragment(frag);
    if (atoms.length === 0) continue;

    // Get bounding box of this fragment
    const bbox = getBoundingBox(atoms);
    const fragWidth = bbox.max.x - bbox.min.x;

    // Translate atoms to position after previous molecules
    const translateX = xOffset - bbox.min.x;
    for (const atom of atoms) {
      allAtoms.push({
        sym: atom.sym,
        x: atom.x + translateX,
        y: atom.y,
        z: atom.z,
      });
    }

    // Update offset for next fragment
    xOffset += fragWidth + MOLECULE_SPACING;
  }

  return atomsToXYZ(allAtoms, `Generated from multi-fragment SMILES: ${smiles}`);
}

